/**
 * Relatórios > Financeiro > Fluxo de Caixa.
 *
 * Realizado: entradas/saídas/saldo por dia, por conta ou consolidado.
 * Projetado: horizonte de 3/6/12 meses com faturas de cartão futuras.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Calendar, CalendarClock, ChevronDown, ChevronRight, CreditCard, Wallet } from 'lucide-react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { clsx } from 'clsx';
import financialReportsService from '@services/financialReportsService';
import type {
  CashflowAccount,
  CashflowConsolidated,
  CashflowDay,
  CashflowProjectedReport,
  CashflowRealizedReport,
  CashflowTransaction,
} from '@/types/financialReports';
import { formatCnpj } from '@/types/company';
import { formatAccountBankInfo } from '@/types/financial';
import { formatCurrencyBRL, formatDateBR } from '@pages/financial/financialUtils';
import {
  ErrorBox,
  LoadingSpinner,
  SummaryCard,
  labelCls,
  primaryBtnCls,
  selectCls,
} from '@pages/financial/financialShared';
import {
  ChartTooltip,
  CompanySelect,
  getMonthOptions,
  getYearOptions,
  kFormatter,
  useCompanies,
} from './financialReportsShared';
import { useUserPreferences, PREF_FIN_REPORT_CASHFLOW } from '@hooks/useUserPreferences';
import { useIsMobile } from '@hooks/useIsMobile';
import { cashflowDayTransactions, cashflowRowKey, monthLabelPtBR, projectedRowDetail } from './financialReportsUtils';

/** Preferências das duas seções, persistidas sob uma única entidade. */
interface CashflowPrefs extends Record<string, unknown> {
  mode?: CashflowMode;
  /** Realizado: contas marcadas (vazio = consolidado). */
  account_ids?: number[];
  /** Projetado: horizonte em meses. */
  months?: number;
  /** Projetado: empresa (ausente = consolidado). */
  company_id?: number;
}

interface CashflowSectionProps {
  initialPrefs: CashflowPrefs;
  onFiltersChange: (patch: Partial<CashflowPrefs>) => void;
}

// ─── Realizado ──────────────────────────────────────────────────

/** Rótulo de uma conta no seletor: documento + nome da empresa, descrição da
 * conta e dados bancários (agência/conta), omitindo o que estiver ausente. */
const cashflowAccountLabel = (a: CashflowAccount): string => {
  const doc = a.companyDocument ? `${formatCnpj(a.companyDocument)} ` : '';
  const company = a.companyName ? `${a.companyName} — ` : '';
  const bank = formatAccountBankInfo(a);
  return `${doc}${company}${a.accountName}${bank ? ` (${bank})` : ''}`;
};

/** Rótulo da conta de um lançamento. O nome sozinho é ambíguo (contas de
 * empresas diferentes se chamam "Conta Corrente"), então usa o cadastro já
 * carregado no relatório para acrescentar empresa e dados bancários. */
const transactionAccountLabel = (
  transaction: CashflowTransaction,
  accountsById: Map<number, CashflowAccount>,
): string => {
  const account = accountsById.get(transaction.idAccountsMain);
  if (!account) return transaction.accountName;
  const bank = formatAccountBankInfo(account);
  const name = [account.companyName, account.accountName].filter(Boolean).join(' — ');
  return `${name}${bank ? ` (${bank})` : ''}`;
};

/** Mescla o fluxo de caixa de várias contas em uma visão combinada, somando
 * entradas/saídas/saldo por dia e os totais. Com uma única conta, retorna-a. */
const mergeCashflowAccounts = (accounts: CashflowAccount[]): CashflowConsolidated => {
  const byDate = new Map<string, CashflowDay>();
  for (const acc of accounts) {
    for (const d of acc.days) {
      const cur = byDate.get(d.date);
      if (cur) {
        cur.in += d.in;
        cur.out += d.out;
        cur.balance += d.balance;
        cur.projectedIn += d.projectedIn;
        cur.projectedBalance += d.projectedBalance;
      } else {
        byDate.set(d.date, { ...d });
      }
    }
  }
  const days = [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));
  const sum = (pick: (a: CashflowAccount) => number) => accounts.reduce((t, a) => t + pick(a), 0);
  return {
    openingBalance: sum(a => a.openingBalance),
    closingBalance: sum(a => a.closingBalance),
    currentBalance: sum(a => a.currentBalance),
    totalIn: sum(a => a.totalIn),
    totalOut: sum(a => a.totalOut),
    totalProjectedIn: sum(a => a.totalProjectedIn),
    projectedClosingBalance: sum(a => a.projectedClosingBalance),
    days,
  };
};



const RealizedSection: React.FC<CashflowSectionProps> = ({ initialPrefs, onFiltersChange }) => {
  const isMobile = useIsMobile();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  // Seleção de uma ou mais contas (inclusive de empresas diferentes). Vazio =
  // consolidado (todas). A seleção é feita client-side sobre report.accounts.
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set(initialPrefs.account_ids ?? []));
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);

  const [report, setReport] = useState<CashflowRealizedReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Lançamentos do mês (expansão por dia) — busca lazy, cache em state.
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
  const [transactions, setTransactions] = useState<CashflowTransaction[] | null>(null);
  const [txLoading, setTxLoading] = useState(false);
  const [txError, setTxError] = useState<string | null>(null);

  // Fecha o menu de contas ao clicar fora.
  useEffect(() => {
    if (!accountMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(e.target as Node)) {
        setAccountMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [accountMenuOpen]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Busca sempre sem accountId: o retorno traz consolidado + todas as
      // contas, e a seleção da conta é feita client-side (sem novo fetch).
      setReport(await financialReportsService.getCashflowRealized(year, month));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar fluxo de caixa realizado');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Período mudou → invalida cache de lançamentos e recolhe os dias.
  useEffect(() => {
    setTransactions(null);
    setTxError(null);
    setExpandedDays(new Set());
  }, [year, month]);

  const fetchTransactions = useCallback(async () => {
    setTxLoading(true);
    setTxError(null);
    try {
      // Busca todos os lançamentos do mês (todas as contas); o filtro pela(s)
      // conta(s) selecionada(s) é feito client-side, como no relatório.
      setTransactions(await financialReportsService.getCashflowTransactions(year, month));
    } catch (err) {
      setTxError(err instanceof Error ? err.message : 'Erro ao carregar lançamentos');
    } finally {
      setTxLoading(false);
    }
  }, [year, month]);

  const toggleDay = (date: string) => {
    setExpandedDays(prev => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
    // Primeira expansão do mês → busca os lançamentos UMA vez (cache em state).
    if (!expandedDays.has(date) && transactions === null && !txLoading) fetchTransactions();
  };

  // Descarta contas salvas que não existem mais antes de repersistir a seleção.
  useEffect(() => {
    if (!report) return;
    const valid = new Set(report.accounts.map(a => a.idAccountsMain));
    setSelectedIds(prev => {
      const next = new Set([...prev].filter(id => valid.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [report]);

  useEffect(() => {
    onFiltersChange({ account_ids: [...selectedIds] });
  }, [selectedIds, onFiltersChange]);

  const allAccounts = report?.accounts ?? [];
  const selectedAccounts = allAccounts.filter(a => selectedIds.has(a.idAccountsMain));
  // Nenhuma conta marcada → consolidado; 1 → a própria conta; 2+ → mescla.
  const singleAccount = selectedAccounts.length === 1 ? selectedAccounts[0] : null;
  const view: CashflowAccount | CashflowConsolidated | null =
    selectedAccounts.length === 0
      ? report?.consolidated ?? null
      : selectedAccounts.length === 1
        ? selectedAccounts[0]
        : mergeCashflowAccounts(selectedAccounts);

  // O lançamento é casado com a conta por idAccountsMain — a mesma chave que
  // agrupa os totais diários. Filtrar por nome não funciona: contas de empresas
  // diferentes se chamam "Conta Corrente".
  const accountsById = new Map(allAccounts.map(a => [a.idAccountsMain, a]));

  const toggleAccount = (id: number) =>
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const accountButtonLabel =
    selectedAccounts.length === 0
      ? 'Consolidado (todas)'
      : selectedAccounts.length === 1
        ? cashflowAccountLabel(selectedAccounts[0])
        : `${selectedAccounts.length} contas selecionadas`;

  const chartData = (view?.days ?? []).map(d => ({
    day: d.date.slice(8, 10),
    Entradas: Math.abs(d.in),
    Saídas: Math.abs(d.out),
    Previsto: Math.abs(d.projectedIn),
    Saldo: d.balance,
    'Saldo com previsão': d.projectedBalance,
  }));

  // Só desenha o que existe: conta sem cartão não precisa de série vazia.
  const hasProjection = (view?.totalProjectedIn ?? 0) !== 0;

  return (
    <div className="space-y-6">
      {/* Filtros */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-full sm:w-auto">
            <label className={labelCls}>Mês</label>
            <select className={`${selectCls} w-full sm:w-32`} value={month} onChange={e => setMonth(Number(e.target.value))}>
              {getMonthOptions().map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="w-full sm:w-auto">
            <label className={labelCls}>Ano</label>
            <select className={`${selectCls} w-full sm:w-28`} value={year} onChange={e => setYear(Number(e.target.value))}>
              {getYearOptions().map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div ref={accountMenuRef} className="relative">
            <label className={labelCls}>Conta(s)</label>
            <button
              type="button"
              onClick={() => setAccountMenuOpen(o => !o)}
              className={`${selectCls} w-full sm:w-72 flex items-center justify-between gap-2 text-left`}
              title={accountButtonLabel}
            >
              <span className="truncate">{accountButtonLabel}</span>
              <ChevronDown size={16} className="shrink-0 text-slate-400" />
            </button>
            {accountMenuOpen && (
              <div className="absolute left-0 z-20 mt-1 w-[calc(100vw-3rem)] sm:w-96 max-h-72 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg p-1">
                <button
                  type="button"
                  onClick={() => setSelectedIds(new Set())}
                  className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-slate-50 text-slate-600"
                >
                  Consolidado (todas)
                </button>
                {allAccounts.length === 0 && (
                  <p className="px-3 py-2 text-sm text-slate-400">Nenhuma conta disponível</p>
                )}
                {allAccounts.map(a => (
                  <label
                    key={a.idAccountsMain}
                    className="flex items-start gap-2 px-3 py-2 text-sm rounded-md hover:bg-slate-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={selectedIds.has(a.idAccountsMain)}
                      onChange={() => toggleAccount(a.idAccountsMain)}
                    />
                    <span className="text-slate-700">{cashflowAccountLabel(a)}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <button onClick={fetchData} disabled={loading} className={`${primaryBtnCls} w-full sm:w-auto justify-center`}>
            <Calendar size={16} />
            {loading ? 'Carregando...' : 'Atualizar'}
          </button>
        </div>
      </div>

      {error && <ErrorBox message={error} onRetry={fetchData} />}
      {loading && <LoadingSpinner />}

      {!loading && !error && view && (
        <>
          {/* Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <SummaryCard label="Saldo Inicial" value={formatCurrencyBRL(view.openingBalance)} />
            <SummaryCard label="Entradas" value={formatCurrencyBRL(view.totalIn)} valueClassName="text-emerald-600" />
            <SummaryCard label="Saídas" value={formatCurrencyBRL(Math.abs(view.totalOut))} valueClassName="text-red-600" />
            <SummaryCard
              label="Saldo Final"
              value={formatCurrencyBRL(view.closingBalance)}
              valueClassName={view.closingBalance < 0 ? 'text-red-600' : 'text-emerald-700'}
            />
            {/* Previsto fica em cartões próprios: é dinheiro a caminho, não
                recebido — somá-lo às Entradas quebraria o batimento com o banco. */}
            <SummaryCard
              label="Previsto a receber no mês"
              value={formatCurrencyBRL(view.totalProjectedIn)}
              valueClassName="text-sky-600"
            />
            <SummaryCard
              label="Saldo Final com Previsão"
              value={formatCurrencyBRL(view.projectedClosingBalance)}
              valueClassName={view.projectedClosingBalance < 0 ? 'text-red-600' : 'text-sky-700'}
            />
            <div className="bg-white border border-indigo-200 rounded-xl shadow-sm p-4">
              <p className="text-sm text-slate-500">Saldo Atual</p>
              <p
                className={clsx(
                  'text-xl font-bold mt-1',
                  view.currentBalance < 0 ? 'text-red-600' : 'text-indigo-700',
                )}
              >
                {formatCurrencyBRL(view.currentBalance)}
              </p>
            </div>
          </div>
          {singleAccount && singleAccount.openingSource && (
            <p className="text-xs text-slate-500 -mt-3">Fonte do saldo inicial: {singleAccount.openingSource}</p>
          )}

          {/* Gráfico diário */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
            <h3 className="text-base font-semibold text-slate-800 mb-3">Movimentação Diária</h3>
            {chartData.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">Nenhuma movimentação no período.</p>
            ) : (
              <div className="h-64 sm:h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="day" tick={{ fontSize: 12, fill: '#64748b' }} interval={isMobile ? 4 : 'preserveEnd'} />
                    <YAxis tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={kFormatter} />
                    <Tooltip content={<ChartTooltip />} />
                    {!isMobile && <Legend />}
                    <Bar dataKey="Entradas" fill="#10b981" radius={[4, 4, 0, 0]} />
                    {hasProjection && <Bar dataKey="Previsto" fill="#0ea5e9" radius={[4, 4, 0, 0]} />}
                    <Bar dataKey="Saídas" fill="#ef4444" radius={[4, 4, 0, 0]} />
                    <Line type="monotone" dataKey="Saldo" stroke="#3b82f6" strokeWidth={2} dot={{ r: 2 }} />
                    {hasProjection && (
                      <Line
                        type="monotone"
                        dataKey="Saldo com previsão"
                        stroke="#0ea5e9"
                        strokeWidth={2}
                        strokeDasharray="5 4"
                        dot={false}
                      />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Tabela diária */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
            <h3 className="text-base font-semibold text-slate-800 mb-3">Detalhamento por Dia</h3>
            {view.days.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">Nenhuma movimentação no período.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-3 font-semibold text-slate-600">Data</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-600">Entradas</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-600">Saídas</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-600">Saldo</th>
                      <th className="text-right py-2 px-3 font-semibold text-sky-700" title="Parcelas que a adquirente ainda vai creditar">
                        Previsto (cartão)
                      </th>
                      <th className="text-right py-2 px-3 font-semibold text-sky-700">Saldo com previsão</th>
                    </tr>
                  </thead>
                  <tbody>
                    {view.days.map(d => {
                      // O previsto conta: dias que só têm crédito de cartão a
                      // caminho também precisam abrir o detalhamento.
                      const hasMovement = d.in !== 0 || d.out !== 0 || d.projectedIn !== 0;
                      const expanded = expandedDays.has(d.date);
                      const dayTransactions = expanded
                        ? cashflowDayTransactions(transactions ?? [], d.date, selectedIds)
                        : [];
                      return (
                        <React.Fragment key={d.date}>
                          <tr
                            onClick={hasMovement ? () => toggleDay(d.date) : undefined}
                            title={hasMovement ? 'Ver lançamentos do dia' : undefined}
                            className={clsx(
                              'border-b border-slate-100 hover:bg-slate-50',
                              hasMovement && 'cursor-pointer',
                            )}
                          >
                            <td className="py-2 px-3 text-slate-700">
                              {hasMovement ? (
                                <span className="inline-flex items-center gap-1.5">
                                  {expanded ? (
                                    <ChevronDown size={15} className="text-slate-400" />
                                  ) : (
                                    <ChevronRight size={15} className="text-slate-400" />
                                  )}
                                  {formatDateBR(d.date)}
                                </span>
                              ) : (
                                <span className="pl-[21px]">{formatDateBR(d.date)}</span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-right text-emerald-600">{formatCurrencyBRL(Math.abs(d.in))}</td>
                            <td className="py-2 px-3 text-right text-red-500">{formatCurrencyBRL(Math.abs(d.out))}</td>
                            <td className={clsx('py-2 px-3 text-right font-medium', d.balance < 0 ? 'text-red-600' : 'text-slate-900')}>
                              {formatCurrencyBRL(d.balance)}
                            </td>
                            <td className="py-2 px-3 text-right text-sky-600">
                              {d.projectedIn !== 0 ? formatCurrencyBRL(Math.abs(d.projectedIn)) : ''}
                            </td>
                            <td className={clsx('py-2 px-3 text-right', d.projectedBalance < 0 ? 'text-red-600' : 'text-sky-700')}>
                              {formatCurrencyBRL(d.projectedBalance)}
                            </td>
                          </tr>
                          {expanded && txLoading && transactions === null && (
                            <tr className="border-b border-slate-100 bg-slate-50/60">
                              <td colSpan={6} className="py-2 px-3 pl-4 sm:pl-12 text-xs text-slate-400">
                                Carregando lançamentos...
                              </td>
                            </tr>
                          )}
                          {expanded && txError && (
                            <tr className="border-b border-slate-100 bg-slate-50/60">
                              <td colSpan={6} className="py-2 px-3 pl-4 sm:pl-12 text-xs text-red-500">
                                {txError}{' '}
                                <button
                                  type="button"
                                  onClick={e => {
                                    e.stopPropagation();
                                    fetchTransactions();
                                  }}
                                  className="font-medium underline hover:text-red-700"
                                >
                                  Tentar novamente
                                </button>
                              </td>
                            </tr>
                          )}
                          {expanded && !txError && transactions !== null && dayTransactions.length === 0 && (
                            <tr className="border-b border-slate-100 bg-slate-50/60">
                              <td colSpan={6} className="py-2 px-3 pl-4 sm:pl-12 text-xs italic text-slate-400">
                                Nenhum lançamento no dia.
                              </td>
                            </tr>
                          )}
                          {expanded &&
                            dayTransactions.map(t => (
                              <tr
                                key={`${d.date}-${cashflowRowKey(t)}`}
                                className={clsx(
                                  'border-b border-slate-100',
                                  t.projected ? 'bg-sky-50/40' : 'bg-slate-50/60',
                                )}
                              >
                                <td className="py-1.5 px-3 pl-4 sm:pl-12 text-slate-600">
                                  {t.projected && (
                                    <span className="inline-flex items-center gap-1 mr-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-sky-100 text-sky-700 align-middle">
                                      <CalendarClock size={10} /> Previsto
                                    </span>
                                  )}
                                  {t.description}
                                  <span className="block text-xs text-slate-400">
                                    {t.projected
                                      ? projectedRowDetail(t)
                                      : `${t.categoryDescription ?? 'Sem categoria'} · ${transactionAccountLabel(t, accountsById)}`}
                                  </span>
                                </td>
                                <td className="py-1.5 px-3 text-right text-emerald-600">
                                  {!t.projected && t.amount > 0 ? formatCurrencyBRL(t.amount) : ''}
                                </td>
                                <td className="py-1.5 px-3 text-right text-red-500">
                                  {!t.projected && t.amount < 0 ? formatCurrencyBRL(Math.abs(t.amount)) : ''}
                                </td>
                                <td />
                                <td className="py-1.5 px-3 text-right text-sky-600">
                                  {t.projected ? formatCurrencyBRL(Math.abs(t.amount)) : ''}
                                </td>
                                <td />
                              </tr>
                            ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-50 font-semibold">
                      <td className="py-2 px-3 text-slate-700">Total</td>
                      <td className="py-2 px-3 text-right text-emerald-600">{formatCurrencyBRL(view.totalIn)}</td>
                      <td className="py-2 px-3 text-right text-red-500">{formatCurrencyBRL(Math.abs(view.totalOut))}</td>
                      <td className={clsx('py-2 px-3 text-right', view.closingBalance < 0 ? 'text-red-600' : 'text-slate-900')}>
                        {formatCurrencyBRL(view.closingBalance)}
                      </td>
                      <td className="py-2 px-3 text-right text-sky-600">{formatCurrencyBRL(view.totalProjectedIn)}</td>
                      <td className={clsx('py-2 px-3 text-right', view.projectedClosingBalance < 0 ? 'text-red-600' : 'text-sky-700')}>
                        {formatCurrencyBRL(view.projectedClosingBalance)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// ─── Projetado ──────────────────────────────────────────────────

const HORIZON_OPTIONS = [3, 6, 12];

const ProjectedSection: React.FC<CashflowSectionProps> = ({ initialPrefs, onFiltersChange }) => {
  const isMobile = useIsMobile();
  const [months, setMonths] = useState(initialPrefs.months ?? 6);
  const [companyId, setCompanyId] = useState<number | null>(initialPrefs.company_id ?? null);
  const { companies, companiesError } = useCompanies();

  const [report, setReport] = useState<CashflowProjectedReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set());

  useEffect(() => {
    onFiltersChange({ months, company_id: companyId ?? undefined });
  }, [months, companyId, onFiltersChange]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReport(await financialReportsService.getCashflowProjected(months, companyId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar fluxo de caixa projetado');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [months, companyId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleMonth = (month: string) =>
    setExpandedMonths(prev => {
      const next = new Set(prev);
      if (next.has(month)) next.delete(month);
      else next.add(month);
      return next;
    });

  const chartData = (report?.months ?? []).map(m => ({
    month: monthLabelPtBR(m.month),
    Entradas: Math.abs(m.inflows),
    Saídas: Math.abs(m.outflows),
    'Saldo Projetado': m.closingBalance,
  }));

  return (
    <div className="space-y-6">
      {/* Filtros */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-full sm:w-auto">
            <label className={labelCls}>Horizonte</label>
            <select className={`${selectCls} w-full sm:w-32`} value={months} onChange={e => setMonths(Number(e.target.value))}>
              {HORIZON_OPTIONS.map(h => (
                <option key={h} value={h}>{h} meses</option>
              ))}
            </select>
          </div>
          <div className="w-full sm:w-auto">
            <label className={labelCls}>Empresa</label>
            <CompanySelect companies={companies} value={companyId} onChange={setCompanyId} />
          </div>
          <button onClick={fetchData} disabled={loading} className={`${primaryBtnCls} w-full sm:w-auto justify-center`}>
            <Calendar size={16} />
            {loading ? 'Carregando...' : 'Atualizar'}
          </button>
        </div>
      </div>

      {companiesError && <ErrorBox message={companiesError} />}
      {error && <ErrorBox message={error} onRetry={fetchData} />}
      {loading && <LoadingSpinner />}

      {!loading && !error && report && (
        report.months.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8 text-center text-sm text-slate-500">
            Nenhuma projeção disponível para o horizonte selecionado.
          </div>
        ) : (
          <>
            {/* Gráfico mensal */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
              <h3 className="text-base font-semibold text-slate-800 mb-3">Projeção Mensal</h3>
              <div className="h-64 sm:h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fontSize: isMobile ? 10 : 12, fill: '#64748b' }} interval={isMobile ? 1 : 0} />
                    <YAxis tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={kFormatter} />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend wrapperStyle={isMobile ? { fontSize: 10 } : undefined} />
                    <Bar dataKey="Entradas" fill="#10b981" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Saídas" fill="#ef4444" radius={[4, 4, 0, 0]} />
                    <Line type="monotone" dataKey="Saldo Projetado" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Tabela mensal com faturas de cartão expansíveis */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
              <h3 className="text-base font-semibold text-slate-800 mb-3">Detalhamento por Mês</h3>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-3 font-semibold text-slate-600">Mês</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-600">Saldo Inicial</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-600">Entradas</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-600">Saídas</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-600">Saldo Final</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.months.map(m => {
                      const expanded = expandedMonths.has(m.month);
                      const hasInvoices = m.creditCardInvoices.length > 0;
                      return (
                        <React.Fragment key={m.month}>
                          <tr className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-2 px-3">
                              {hasInvoices ? (
                                <button
                                  type="button"
                                  onClick={() => toggleMonth(m.month)}
                                  className="inline-flex items-center gap-1.5 font-medium text-slate-700 hover:text-emerald-700"
                                  title="Ver faturas de cartão do mês"
                                >
                                  {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                                  {monthLabelPtBR(m.month)}
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-700">
                                    <CreditCard size={11} /> {m.creditCardInvoices.length}
                                  </span>
                                </button>
                              ) : (
                                <span className="pl-[21px] font-medium text-slate-700">{monthLabelPtBR(m.month)}</span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-right text-slate-700">{formatCurrencyBRL(m.openingBalance)}</td>
                            <td className="py-2 px-3 text-right text-emerald-600">
                              {formatCurrencyBRL(Math.abs(m.inflows))}
                              {/* Quanto do mês depende da adquirente: o detalhe
                                  está na aba Recebíveis de Cartão. */}
                              {m.cardReceivables > 0 && (
                                <span className="block text-[11px] text-slate-400 font-normal">
                                  inclui {formatCurrencyBRL(m.cardReceivables)} de cartão
                                </span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-right text-red-500">{formatCurrencyBRL(Math.abs(m.outflows))}</td>
                            <td className={clsx('py-2 px-3 text-right font-medium', m.closingBalance < 0 ? 'text-red-600' : 'text-slate-900')}>
                              {formatCurrencyBRL(m.closingBalance)}
                            </td>
                          </tr>
                          {expanded &&
                            m.creditCardInvoices.map((inv, idx) => (
                              <tr key={`${m.month}-${idx}`} className="border-b border-slate-100 bg-violet-50/40">
                                <td className="py-1.5 px-3 pl-4 sm:pl-12 text-slate-600" colSpan={2}>
                                  <span className="inline-flex items-center gap-1.5">
                                    <CreditCard size={13} className="text-violet-500" />
                                    {inv.cardName}
                                  </span>
                                </td>
                                <td className="py-1.5 px-3 text-right text-slate-500" colSpan={1}>
                                  Venc.: {formatDateBR(inv.dueDate)}
                                </td>
                                <td className="py-1.5 px-3 text-right text-red-500">{formatCurrencyBRL(Math.abs(inv.amount))}</td>
                                <td />
                              </tr>
                            ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )
      )}
    </div>
  );
};

// ─── View principal (sub-toggle Realizado/Projetado) ────────────

type CashflowMode = 'realizado' | 'projetado';

export const CashflowReportView: React.FC = () => {
  const [mode, setMode] = useState<CashflowMode>('realizado');

  // As duas seções compartilham uma única preferência. O pai é o dono do objeto
  // persistido: as seções mandam patches parciais, senão uma sobrescreveria a outra.
  const { prefs, loaded: prefsLoaded, save: savePrefs } = useUserPreferences<CashflowPrefs>(PREF_FIN_REPORT_CASHFLOW);
  const [filtersReady, setFiltersReady] = useState(false);
  const currentPrefs = useRef<CashflowPrefs>({});

  useEffect(() => {
    if (!prefsLoaded) return;
    if (prefs?.mode === 'realizado' || prefs?.mode === 'projetado') setMode(prefs.mode);
    currentPrefs.current = { ...prefs };
    setFiltersReady(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefsLoaded]);

  const patchPrefs = useCallback(
    (patch: Partial<CashflowPrefs>) => {
      currentPrefs.current = { ...currentPrefs.current, ...patch };
      savePrefs(currentPrefs.current);
    },
    [savePrefs],
  );

  useEffect(() => {
    if (!filtersReady) return;
    patchPrefs({ mode });
  }, [filtersReady, mode, patchPrefs]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Wallet size={18} className="text-emerald-600" />
        <div className="inline-flex w-full sm:w-auto rounded-lg border border-slate-300 overflow-hidden">
          {(['realizado', 'projetado'] as CashflowMode[]).map(m => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={clsx(
                'flex-1 sm:flex-none px-4 py-2 text-sm font-medium transition-colors',
                mode === m ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50',
              )}
            >
              {m === 'realizado' ? 'Realizado' : 'Projetado'}
            </button>
          ))}
        </div>
      </div>

      {/* Só monta a seção depois de restaurar os filtros, para não buscar duas vezes. */}
      {!filtersReady ? null : mode === 'realizado' ? (
        <RealizedSection initialPrefs={currentPrefs.current} onFiltersChange={patchPrefs} />
      ) : (
        <ProjectedSection initialPrefs={currentPrefs.current} onFiltersChange={patchPrefs} />
      )}
    </div>
  );
};

export default CashflowReportView;
